/**
 * FIZITI Quick View
 * Opens a product modal without leaving the current page
 */

(function () {
  'use strict';

  const overlay = document.getElementById('qv-overlay');
  const backdrop = document.getElementById('qv-backdrop');
  const closeBtn = document.getElementById('qv-close');
  const loadingEl = document.getElementById('qv-loading');
  const bodyEl = document.getElementById('qv-body');
  const imagesEl = document.getElementById('qv-images');
  const infoEl = document.getElementById('qv-info');

  if (!overlay) return;

  /* ── Open / Close ──────────────────────────────────────────── */
  function openQV() {
    // 用 rAF 延迟一帧，确保当前 click 事件传播完成后再开启
    requestAnimationFrame(() => {
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    });
  }

  function closeQV() {
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(() => {
      if (loadingEl) { loadingEl.style.display = 'grid'; }
      if (bodyEl) { bodyEl.style.display = 'none'; }
    }, 350);
  }

  closeBtn?.addEventListener('click', e => { e.stopPropagation(); closeQV(); });

  // backdrop 只在直接点击时关闭（不响应冒泡）
  backdrop?.addEventListener('click', e => {
    if (e.target === backdrop) closeQV();
  });

  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeQV(); });

  /* ── Helpers ───────────────────────────────────────────────── */
  // 使用店铺货币格式，不硬编码 $
  const _moneyFmt = window.FIZITI_MONEY_FORMAT || '{{amount}}';
  function money(cents) {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return _moneyFmt.replace(/\{\{\s*amount\s*\}\}/, amount)
                    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
  }

  /* ── Helpers for image URL (兼容 Liquid JSON 对象 & AJAX 字符串) ── */
  function imgSrc(img, size) {
    const src = typeof img === 'string' ? img : (img && img.src ? img.src : '');
    if (!src) return '';
    return src.replace(/(\.[^.?]+)(\?.*)?$/, `_${size}$1`);
  }

  /* ── Load & render product ──────────────────────────────────── */
  let currentHandle = null;
  let variantData = [];
  let selectedOptions = {};

  async function loadProduct(handle, productId) {
    currentHandle = handle;
    variantData = [];
    selectedOptions = {};

    if (loadingEl) loadingEl.style.display = 'grid';
    if (bodyEl) bodyEl.style.display = 'none';
    openQV();

    try {
      let product = null;

      // 优先从页面内嵌的 JSON 读取（无网络请求，更可靠）
      if (productId) {
        const scriptEl = document.getElementById(`pd-${productId}`);
        if (scriptEl) {
          try {
            product = JSON.parse(scriptEl.textContent);
            console.log('[QV] Loaded from inline JSON, id=', productId, 'images:', product.images && product.images.length);
          } catch (parseErr) {
            console.warn('[QV] Inline JSON parse failed:', parseErr);
          }
        } else {
          console.warn('[QV] Script element not found: pd-' + productId);
        }
      }

      // 降级：AJAX fetch
      if (!product) {
        console.log('[QV] Falling back to AJAX for handle:', handle);
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        product = await res.json();
        console.log('[QV] AJAX loaded, images type:', typeof product.images[0]);
      }

      variantData = product.variants;
      renderProduct(product);

    } catch (err) {
      console.error('Quick View failed:', err);
      if (loadingEl) {
        loadingEl.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;
                      height:100%;gap:12px;padding:48px;grid-column:1/-1;text-align:center">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p style="font-size:14px;font-weight:600;color:#64748b;margin:0">Couldn't load product</p>
            <a href="/products/${handle}" style="font-size:13px;color:#2563eb;text-decoration:none">
              View full details →
            </a>
          </div>`;
        loadingEl.style.display = 'grid';
      }
    }
  }

  function renderProduct(product) {
    const firstVariant = product.variants[0];
    const onSale = firstVariant.compare_at_price > firstVariant.price;

    // Pre-select first option values
    product.options.forEach((opt, i) => {
      selectedOptions[i] = product.variants[0].options[i];
    });

    /* ── Images ── */
    const mainImg = product.images[0];
    const thumbsHtml = product.images.length > 1
      ? `<div class="qv-thumbs">${product.images.slice(0, 5).map((img, i) => `
          <button class="qv-thumb ${i === 0 ? 'is-active' : ''}" data-src="${imgSrc(img, '600x')}" aria-label="View image ${i + 1}">
            <img src="${imgSrc(img, '80x')}" alt="" loading="lazy" width="48" height="48">
          </button>`).join('')}
        </div>` : '';

    if (imagesEl) {
      imagesEl.innerHTML = `
        <img id="qv-main-img" class="qv-main-img" src="${imgSrc(mainImg, '800x')}" alt="${product.title}">
        ${thumbsHtml}`;
    }

    /* ── Options ── */
    const optionsHtml = product.options.length === 1 && product.options[0] === 'Title'
      ? ''
      : `<div class="qv-options">
          ${product.options.map((opt, optIdx) => `
            <div>
              <div class="qv-option-label">${opt}</div>
              <div class="qv-option-btns">
                ${[...new Set(product.variants.map(v => v.options[optIdx]))].map(val => {
                  const available = product.variants.some(v => v.options[optIdx] === val && v.available);
                  return `<button class="qv-opt-btn ${val === selectedOptions[optIdx] ? 'is-active' : ''} ${!available ? 'qv-opt-btn--unavailable' : ''}"
                    data-option-index="${optIdx}" data-option-value="${val}"
                    ${!available ? 'aria-label="' + val + ' — unavailable"' : ''}>${val}</button>`;
                }).join('')}
              </div>
            </div>`).join('')}
        </div>`;

    /* ── Price ── */
    const priceHtml = onSale
      ? `<span class="qv-price__current qv-price__current--sale">${money(firstVariant.price)}</span>
         <s class="qv-price__compare">${money(firstVariant.compare_at_price)}</s>
         <span class="qv-price__save">Save ${money(firstVariant.compare_at_price - firstVariant.price)}</span>`
      : `<span class="qv-price__current">${money(firstVariant.price)}</span>`;

    /* ── Info ── */
    if (infoEl) {
      infoEl.innerHTML = `
        ${product.tags.includes('best-seller') ? '<span class="qv-badge">Best Seller</span>' : ''}
        <h2 class="qv-title">${product.title}</h2>
        <div class="qv-price" id="qv-price">${priceHtml}</div>
        ${optionsHtml}
        <button class="qv-atc" id="qv-atc-btn" ${firstVariant.available ? '' : 'disabled'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.61L23 6H6"/></svg>
          ${firstVariant.available ? 'Add to Cart' : 'Sold Out'}
        </button>
        <a href="/products/${product.handle}" class="qv-view-link">View full details →</a>`;
    }

    // Show content
    if (loadingEl) loadingEl.style.display = 'none';
    if (bodyEl) bodyEl.style.display = 'grid';
  }

  /* ── 事件委托（绑定一次，不随产品渲染重复绑定）───────────────── */
  overlay?.addEventListener('click', async function (e) {
    // Thumbnails
    const thumb = e.target.closest('.qv-thumb');
    if (thumb) {
      overlay.querySelectorAll('.qv-thumb').forEach(b => b.classList.remove('is-active'));
      thumb.classList.add('is-active');
      const mainImg = document.getElementById('qv-main-img');
      if (mainImg) mainImg.src = thumb.dataset.src;
      return;
    }

    // Option buttons
    const optBtn = e.target.closest('.qv-opt-btn:not(.qv-opt-btn--unavailable)');
    if (optBtn) {
      const idx = parseInt(optBtn.dataset.optionIndex);
      const val = optBtn.dataset.optionValue;
      overlay.querySelectorAll(`.qv-opt-btn[data-option-index="${idx}"]`)
        .forEach(b => b.classList.remove('is-active'));
      optBtn.classList.add('is-active');
      selectedOptions[idx] = val;
      if (variantData.length) updateVariant();
      return;
    }

    // ATC button
    const atcBtn = e.target.closest('#qv-atc-btn');
    if (atcBtn) {
      const match = findVariant();
      if (!match || !match.available) return;

      const origHtml = atcBtn.innerHTML;
      atcBtn.innerHTML = `<svg class="spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>Adding…`;
      atcBtn.disabled = true;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: match.id, quantity: 1 })
        });
        if (!res.ok) throw new Error();

        window.FizitiCart?.refresh();

        atcBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Added!`;
        atcBtn.style.background = '#16a34a';

        setTimeout(() => {
          closeQV();
          window.FizitiCart?.open();
        }, 900);
      } catch {
        atcBtn.innerHTML = origHtml;
        atcBtn.disabled = false;
      }
    }
  });


  function findVariant() {
    const vals = Object.keys(selectedOptions).sort().map(k => selectedOptions[k]);
    return variantData.find(v => v.options.every((o, i) => o === vals[i]));
  }

  function updateVariant() {
    const match = findVariant();
    if (!match) return;

    const priceEl = document.getElementById('qv-price');
    const atcBtn = document.getElementById('qv-atc-btn');

    if (priceEl) {
      const onSale = match.compare_at_price > match.price;
      priceEl.innerHTML = onSale
        ? `<span class="qv-price__current qv-price__current--sale">${money(match.price)}</span>
           <s class="qv-price__compare">${money(match.compare_at_price)}</s>`
        : `<span class="qv-price__current">${money(match.price)}</span>`;
    }

    if (atcBtn) {
      atcBtn.disabled = !match.available;
      atcBtn.innerHTML = match.available
        ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.61L23 6H6"/></svg>Add to Cart`
        : 'Sold Out';
    }

    // Switch image to variant image if available
    if (match.featured_image) {
      const mainImg = document.getElementById('qv-main-img');
      if (mainImg) mainImg.src = imgSrc(match.featured_image, '800x');
    }
  }

  /* ── Delegated click on Quick View buttons ───────────────────── */
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.quickView;
    const productId = btn.dataset.productId;
    if (handle) loadProduct(handle, productId);
  });

  /* ── Expose API ──────────────────────────────────────────────── */
  window.FizitiQuickView = { open: loadProduct, close: closeQV };
})();
